"use client";

import { useState } from "react";
import Image, { type StaticImageData } from "next/image";
import { cn } from "@/lib/utils";
import { Dialog, DialogClose, DialogContent, DialogTrigger } from "@/shadcn/ui/dialog";
import { ArrowUpRightIcon } from "../pricingSectionV2/PricingPlatformsBlock";
import { resolveNewBadgeLabel } from "../pricingSectionV2/helpers";

import logoTradovate from "@/public/new-pricing/logo-tradovate.png";
import logoTradingview from "@/public/new-pricing/logo-tradingview.png";
import logoNinjatrader from "@/public/new-pricing/logo-ninjatrader.png";
import shotTradovate from "@/public/new-pricing/platform-dialog/shot-tradovate.webp";
import shotTradingview from "@/public/new-pricing/platform-dialog/shot-tradingview.webp";
import shotNinjatrader from "@/public/new-pricing/platform-dialog/shot-ninjatrader.webp";
import bgTexture from "@/public/new-pricing/platform-dialog/bg-texture.webp";

/**
 * Content of the special Tradovate platform dialog (Figma 6171:1496 /
 * 6174:2216 / 6174:2261): the three bundled tools are switchable inside the
 * dialog — Tradovate (default), TradingView, NinjaTrader.
 */

type TabId = "tradovate" | "tradingview" | "ninjatrader";

const TAB_ORDER: TabId[] = ["tradovate", "tradingview", "ninjatrader"];

/** Hard-coded EN/DE dictionaries, per the repo's no-i18n-lib pattern.
 *  Platform names and trading terms stay in English (spec translation rules). */
const COPY: Record<
  string,
  { bullets: Record<TabId, string[]>; cta: string }
> = {
  en: {
    bullets: {
      tradovate: [
        "Commission-free futures trading",
        "Cloud-based — trade from any browser",
        "No platform or data fees",
        "Fast, modern, streamlined interface",
      ],
      tradingview: [
        "The world’s most popular charts",
        "100+ indicators & drawing tools",
        "Multi-device sync & price alerts",
        "Huge community of scripts & ideas",
      ],
      ninjatrader: [
        "Advanced futures charting & analytics",
        "Order-flow & market-depth tools",
        "Automated strategy & backtesting",
        "Free, award-winning desktop platform",
      ],
    },
    cta: "Back to website",
  },
  de: {
    bullets: {
      tradovate: [
        "Futures-Trading ohne Kommissionen",
        "Cloud-basiert — trade aus jedem Browser",
        "Keine Plattform- oder Datengebühren",
        "Schnelles, modernes, aufgeräumtes Interface",
      ],
      tradingview: [
        "Die beliebtesten Charts der Welt",
        "100+ Indikatoren & Zeichentools",
        "Multi-Device-Sync & Preisalarme",
        "Riesige Community mit Scripts & Ideen",
      ],
      ninjatrader: [
        "Fortgeschrittenes Futures-Charting & Analytics",
        "Order-Flow- & Markttiefe-Tools",
        "Automatisierte Strategien & Backtesting",
        "Kostenlose, preisgekrönte Desktop-Plattform",
      ],
    },
    cta: "Zurück zur Website",
  },
};

const TABS: Record<
  TabId,
  {
    logo: StaticImageData;
    logoClass: string;
    shot: StaticImageData;
    diamond: string;
    label: string;
  }
> = {
  tradovate: {
    logo: logoTradovate,
    logoClass: "h-auto w-[14.5rem]",
    shot: shotTradovate,
    diamond: "#267fff",
    label: "Tradovate",
  },
  tradingview: {
    logo: logoTradingview,
    logoClass: "h-auto w-[17.66rem]",
    shot: shotTradingview,
    diamond: "#103037",
    label: "TradingView",
  },
  ninjatrader: {
    logo: logoNinjatrader,
    logoClass: "h-auto w-[9.29rem]",
    shot: shotNinjatrader,
    diamond: "#ff4200",
    label: "NinjaTrader",
  },
};

/**
 * Code-owned Tradovate platform card (Figma 6114:447) — same chrome as the
 * Sanity-driven cards, tagged "New", opens the tabbed dialog. Injected into
 * the futures group via PricingPlatformsBlock's `groupExtras` (the platform
 * does not exist in the Sanity document).
 */
export function TradovatePlatformCard({ language }: { language?: string }) {
  return (
    <Dialog>
      <DialogTrigger asChild>
        <button
          type="button"
          className="w-full cursor-pointer text-left">
          <div className="group relative flex w-full items-center justify-between gap-[1.25rem] rounded-[0.5rem] border border-black/[0.12] bg-[#f4f4f4] py-[1.25rem] pr-[1.25rem] pl-[2.25rem] transition-colors hover:bg-[#efefef] md:gap-[1.5rem] md:py-[1.75rem] md:pr-[1.75rem] md:pl-[3rem]">
            <div className="flex h-[3rem] w-[16rem] shrink-0 items-center justify-start md:h-[3.75rem] md:w-[18rem]">
              <Image
                src={logoTradovate}
                alt="Tradovate"
                className="h-auto w-[14.5rem]"
              />
            </div>
            <div className="inline-flex shrink-0 items-center justify-center rounded-[0.25rem] bg-white p-[1rem] text-black transition-colors group-hover:bg-black group-hover:text-white md:p-[1.25rem]">
              <ArrowUpRightIcon />
            </div>
            <span className="pointer-events-none absolute -top-[1.25rem] -right-[0.5rem] inline-flex items-center justify-center rounded-[7.5rem] bg-[#1aa87f] px-[1rem] py-[0.375rem] font-geist text-[1.5rem] leading-[1.3] font-normal tracking-[-0.075rem] whitespace-nowrap text-white shadow-sm md:-top-[1.5rem] md:-right-[0.75rem] md:px-[1.125rem] md:py-[0.5rem] md:text-[1.75rem] md:tracking-[-0.0875rem]">
              {resolveNewBadgeLabel(language)}
            </span>
          </div>
        </button>
      </DialogTrigger>
      <DialogContent className="max-h-[90dvh] !w-[min(120rem,calc(100%-4rem))] overflow-y-auto rounded-[0.75rem] bg-white p-[2rem] pb-[3.75rem]">
        <TradovatePlatformDialogContent language={language} />
      </DialogContent>
    </Dialog>
  );
}

export default function TradovatePlatformDialogContent({ language }: { language?: string }) {
  const copy = COPY[language ?? "en"] ?? COPY.en;
  const [tab, setTab] = useState<TabId>("tradovate");
  const active = TABS[tab];

  return (
    <div className="flex w-full flex-col items-center gap-[3rem] pb-[1.75rem]">
      {/* screenshot area */}
      <div className="relative aspect-[615/335] w-full overflow-hidden rounded-[0.5rem] bg-[#f5f5f5]/60">
        <div
          className="pointer-events-none absolute inset-0 opacity-10 mix-blend-difference"
          aria-hidden="true">
          <Image
            src={bgTexture}
            alt=""
            fill
            sizes="80rem"
            className="object-cover object-top"
          />
        </div>
        <Image
          key={tab}
          src={active.shot}
          alt={active.label}
          className="absolute top-1/2 left-1/2 w-[78.5%] -translate-x-1/2 -translate-y-1/2 rounded-[0.25rem]"
        />
      </div>

      {/* tool switcher */}
      <div
        role="tablist"
        className="flex w-full items-stretch gap-[0.75rem]">
        {TAB_ORDER.map((id) => {
          const t = TABS[id];
          const activeTab = id === tab;
          return (
            <button
              key={id}
              type="button"
              role="tab"
              aria-selected={activeTab}
              aria-label={t.label}
              onClick={() => setTab(id)}
              className={cn(
                "flex h-[10.35rem] min-w-0 flex-1 items-center justify-center rounded-[1rem] px-[2.25rem] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#307f6a]",
                activeTab ? "border-2 border-[#307f6a] bg-[#f5f9f8]" : "bg-[#f1f1f1] hover:bg-[#e9e9e9]",
              )}>
              <Image
                src={t.logo}
                alt={t.label}
                className={t.logoClass}
              />
            </button>
          );
        })}
      </div>

      {/* bullets + CTA */}
      <div className="flex w-full flex-col gap-[4.5rem] px-[1.75rem]">
        <div className="flex flex-col gap-[1.25rem]">
          {copy.bullets[tab].map((text) => (
            <div
              key={text}
              className="flex items-center gap-[1.25rem]">
              <span
                className="flex h-[1.375rem] w-[1.375rem] shrink-0 items-center justify-center"
                aria-hidden="true">
                <span
                  className="block h-[0.97rem] w-[0.97rem] rotate-45"
                  style={{ backgroundColor: active.diamond }}
                />
              </span>
              <p className="mb-0 font-geist text-[2.25rem] leading-[1.5] font-normal text-[#103037]">{text}</p>
            </div>
          ))}
        </div>

        <DialogClose asChild>
          <button
            type="button"
            className="inline-flex w-full items-center justify-center rounded-[0.25rem] border border-black/[0.12] bg-black px-[3rem] py-[2.25rem] font-instrument-sans text-[2rem] leading-[1.35] font-semibold tracking-[-0.04rem] text-white capitalize transition-colors hover:bg-black/90">
            {copy.cta}
          </button>
        </DialogClose>
      </div>
    </div>
  );
}
